import api from "@/utils/api";

const SESSION_KEY = "tvet_public_traffic_sid";
const QUEUE_KEY = "tvet_public_traffic_queue";
const FLUSH_INTERVAL_MS = 4000;
const MAX_QUEUE = 40;

let installed = false;
let lastPageviewKey = "";
let listenersBound = false;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const isPublicPath = (path) => {
  const value = String(path || "/").split("?")[0].split("#")[0] || "/";
  const normalized = value.startsWith("/") ? value : `/${value}`;
  const lower = normalized.toLowerCase();
  return !(
    lower.startsWith("/admin") ||
    lower.startsWith("/api") ||
    lower.startsWith("/storage") ||
    lower.startsWith("/sanctum")
  );
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 32);
  }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

const getSessionId = () => {
  if (!isBrowser()) return createSessionId();
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = createSessionId();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return createSessionId();
  }
};

const detectDeviceType = () => {
  if (!isBrowser()) return "unknown";
  const width = window.innerWidth || 0;
  if (width < 768) return "mobile";
  if (width < 1200) return "tablet";
  return "desktop";
};

const detectBrowser = () => {
  if (!isBrowser()) return "Unknown";
  const ua = navigator.userAgent || "";
  if (/edg\//i.test(ua)) return "Edge";
  if (/chrome|crios/i.test(ua) && !/edg\//i.test(ua)) return "Chrome";
  if (/firefox|fxios/i.test(ua)) return "Firefox";
  if (/safari/i.test(ua) && !/chrome|crios|android/i.test(ua)) return "Safari";
  if (/opr\//i.test(ua)) return "Opera";
  return "Other";
};

const detectPlatform = () => {
  if (!isBrowser()) return "Unknown";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad|ipod/i.test(ua)) return "iOS";
  if (/win/i.test(ua)) return "Windows";
  if (/mac/i.test(ua)) return "macOS";
  if (/linux/i.test(ua)) return "Linux";
  return "Other";
};

const readQueue = () => {
  if (!isBrowser()) return [];
  try {
    const raw = sessionStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = (items) => {
  if (!isBrowser()) return;
  try {
    sessionStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-MAX_QUEUE)));
  } catch {
    /* ignore quota errors */
  }
};

const enqueue = (event) => {
  if (!isPublicPath(event.page_path)) return;
  const queue = readQueue();
  queue.push(event);
  writeQueue(queue);
  if (queue.length >= 8) {
    flushQueue();
  }
};

const buildBaseContext = () => {
  if (!isBrowser()) {
    return {
      session_id: getSessionId(),
      page_path: "/",
      page_title: "",
      page_url: "",
      referrer: "",
      device_type: "unknown",
      browser: "Unknown",
      platform: "Unknown",
      screen_width: null,
      screen_height: null,
      language: "en",
      occurred_at: new Date().toISOString(),
    };
  }

  return {
    session_id: getSessionId(),
    page_path: window.location.pathname || "/",
    page_title: document.title || "",
    page_url: window.location.href || "",
    referrer: document.referrer || "",
    device_type: detectDeviceType(),
    browser: detectBrowser(),
    platform: detectPlatform(),
    screen_width: window.screen?.width || window.innerWidth || null,
    screen_height: window.screen?.height || window.innerHeight || null,
    language: navigator.language || "en",
    occurred_at: new Date().toISOString(),
  };
};

export const trackPublicEvent = (partial = {}) => {
  const base = buildBaseContext();
  const pagePath = partial.page_path || base.page_path;
  if (!isPublicPath(pagePath)) return;

  enqueue({
    ...base,
    ...partial,
    page_path: pagePath,
    event_type: partial.event_type || "custom",
  });
};

export const trackPageview = (route) => {
  if (!isBrowser()) return;
  const path = route?.fullPath
    ? String(route.fullPath).split("?")[0].split("#")[0]
    : window.location.pathname;
  if (!isPublicPath(path)) return;

  const key = `${path}|${document.title || ""}`;
  if (key === lastPageviewKey) return;
  lastPageviewKey = key;

  trackPublicEvent({
    event_type: "pageview",
    event_name: route?.name ? String(route.name) : "page_visit",
    page_path: path,
    page_title: document.title || route?.meta?.title || "",
    page_url: window.location.href,
    metadata: {
      route_name: route?.name || null,
    },
  });
};

const closestInteractive = (target) => {
  if (!(target instanceof Element)) return null;
  return target.closest(
    "button, a, [role='button'], input[type='submit'], .v-btn, [data-track]",
  );
};

const onDocumentClick = (event) => {
  if (!isPublicPath(window.location.pathname)) return;
  const el = closestInteractive(event.target);
  if (!el) return;

  const tag = (el.tagName || "").toLowerCase();
  const text = (el.getAttribute("aria-label") || el.innerText || el.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);
  const href = el.getAttribute("href") || el.getAttribute("to") || null;
  const isOutbound =
    href &&
    /^https?:\/\//i.test(href) &&
    !href.includes(window.location.host);

  trackPublicEvent({
    event_type: isOutbound ? "outbound" : "click",
    event_name:
      el.getAttribute("data-track") ||
      el.getAttribute("name") ||
      text ||
      tag,
    element_tag: tag,
    element_id: el.id || null,
    element_text: text || null,
    element_href: href,
    metadata: {
      classes: typeof el.className === "string" ? el.className.slice(0, 200) : null,
    },
  });
};

const onDocumentSubmit = (event) => {
  if (!isPublicPath(window.location.pathname)) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const formName =
    form.getAttribute("name") ||
    form.id ||
    form.getAttribute("data-track") ||
    "form";

  trackPublicEvent({
    event_type: "form_submit",
    event_name: formName,
    element_tag: "form",
    element_id: form.id || null,
    element_text: formName,
    metadata: {
      action: form.getAttribute("action") || null,
      method: (form.getAttribute("method") || "get").toLowerCase(),
      field_count: form.elements?.length || 0,
    },
  });
};

export const flushQueue = async () => {
  const queue = readQueue();
  if (!queue.length) return;

  writeQueue([]);
  try {
    await api.post("/api/v2/public/traffic", { events: queue }, {
      // Avoid attaching auth headers noise; public endpoint.
      timeout: 8000,
    });
  } catch {
    // Re-queue on failure so we don't lose events silently.
    const existing = readQueue();
    writeQueue([...queue, ...existing].slice(-MAX_QUEUE));
  }
};

const bindListeners = () => {
  if (!isBrowser() || listenersBound) return;
  listenersBound = true;
  document.addEventListener("click", onDocumentClick, true);
  document.addEventListener("submit", onDocumentSubmit, true);
  window.addEventListener("pagehide", () => {
    flushQueue();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushQueue();
  });
};

/**
 * Install public-site traffic tracking (pageviews + clicks + form submits).
 * Skips /admin and other non-public paths.
 */
export const installPublicTrafficTracking = (router) => {
  if (!isBrowser() || installed) return;
  installed = true;
  bindListeners();

  if (router?.afterEach) {
    router.afterEach((to) => {
      // Defer so document.title updates from the destination view.
      setTimeout(() => trackPageview(to), 80);
    });
  } else {
    trackPageview();
  }

  setInterval(() => {
    flushQueue();
  }, FLUSH_INTERVAL_MS);

  // Initial flush of any leftover queue.
  flushQueue();
};

export const usePublicTrafficTracking = () => ({
  trackPublicEvent,
  trackPageview,
  flushQueue,
  installPublicTrafficTracking,
  isPublicPath,
});

export default usePublicTrafficTracking;
